import React, { useState, useEffect, useCallback } from 'react';
import { ListRenderItem, Pressable } from 'react-native';

import { SearchModal } from './search-modal';

import { Portal, useTheme, Text } from '@/components/ui';
import { dataService } from '@/services/data-service';
import { typeaheadSearchStyles as styles } from '@/styles';
import type { Association } from '@/types/models';

export type AssociationsSearchModalProps = {
	visible: boolean;
	onDismiss: () => void;
	onSelect: (association: Association | null) => void;
	placeholder?: string;
	selectedAssociation?: Association | null;
};

export const AssociationsSearchModal: React.FC<AssociationsSearchModalProps> = ({
	visible,
	onDismiss,
	onSelect,
	placeholder = 'Search associations...',
	selectedAssociation,
}) => {
	const theme = useTheme();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | undefined>();
	const [associations, setAssociations] = useState<Association[]>([]);
	const [searchQuery, setSearchQuery] = useState('');

	const searchAssociations = useCallback(async (query: string) => {
		setLoading(true);
		setError(undefined);

		try {
			const response = await dataService.getAssociations({
				search: query,
				status: 'active',
				limit: 50,
			});
			setAssociations(response.data);
		} catch (err) {
			console.error('Error searching associations:', err);
			setError('Failed to search associations');
			setAssociations([]);
		} finally {
			setLoading(false);
		}
	}, []);

	// Debounced search
	useEffect(() => {
		const timer = setTimeout(() => {
			searchAssociations(searchQuery);
		}, 300);

		return () => clearTimeout(timer);
	}, [searchQuery, searchAssociations]);

	// Load initial associations when modal opens
	useEffect(() => {
		if (visible) {
			searchAssociations('');
		}
	}, [visible, searchAssociations]);

	const handleSelect = useCallback((association: Association) => {
		onSelect(association);
		onDismiss();
	}, [onSelect, onDismiss]);

	const handleClear = useCallback(() => {
		onSelect(null);
		onDismiss();
	}, [onSelect, onDismiss]);

	// Convert associations to the format expected by SearchModal
	const filteredOptions = associations.map(association => ({
		id: association.id,
		label: association.name,
		description: [association.address, association.city, association.state]
			.filter(Boolean)
			.join(', '),
		association, // Include the full association object
	}));

	// Add clear option if there's a selected association
	const allOptions = selectedAssociation ? [
		{
			id: 'clear',
			label: 'Clear Selection',
			description: 'Remove current selection',
			association: null,
		},
		...filteredOptions,
	] : filteredOptions;

	const renderOptionItem: ListRenderItem<typeof allOptions[0]> = useCallback(({ item }) => {
		const isClearOption = item.id === 'clear';
		const isSelected = selectedAssociation?.id === item.id;

		return (
			<Pressable
				onPress={() => isClearOption ? handleClear() : (item.association ? handleSelect(item.association) : null)}
				style={[
					styles.modalOptionItem,
					isSelected && { backgroundColor: theme.colors.primaryContainer },
				]}
			>
				<Text
					variant="bodyLarge"
					style={{
						fontWeight: isClearOption ? 'bold' : 'normal',
						color: isClearOption ? theme.colors.error : (isSelected ? theme.colors.onPrimaryContainer : theme.colors.onSurface),
					}}
				>
					{isClearOption ? item.label : item.label}
				</Text>
				{item.description && !isClearOption && (
					<Text
						variant="bodySmall"
						style={{
							color: isSelected ? theme.colors.onPrimaryContainer : theme.colors.onSurfaceVariant,
						}}
					>
						{item.description}
					</Text>
				)}
			</Pressable>
		);
	}, [theme, selectedAssociation, handleSelect, handleClear]);

	if (!visible) return null;

	return (
		<Portal>
			<SearchModal
				isModalVisible={visible}
				closeSearch={onDismiss}
				placeholder={placeholder}
				onChangeText={setSearchQuery}
				searchQuery={searchQuery}
				filteredOptions={allOptions}
				renderOptionItem={renderOptionItem}
			/>
		</Portal>
	);
};
